import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { ChevronLeft, Lock } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUnsavedChangesGuard } from "@/hooks/use-unsaved-changes-guard";
import {
  getGetInventoryPlanQueryKey,
  getListInventoryItemsQueryKey,
  getListInventoryPlanPeriodsQueryKey,
  getListPlanEntitlementsQueryKey,
  getListPlanSecondaryAccessQueryKey,
  useCreateInventoryPlan,
  useCreatePlanEntitlement,
  useDeleteInventoryPlanPeriod,
  useDeleteInventoryPlanPeriodById,
  useDeletePlanSecondaryAccess,
  useGetInventoryPlan,
  useListInventoryItems,
  useListInventoryPlanPeriods,
  useListPlanEntitlements,
  useListPlanSecondaryAccess,
  useUpdateInventoryPlan,
  useUpdatePlanEntitlement,
  useUpsertInventoryPlanPeriod,
  useUpsertPlanSecondaryAccess,
} from "@workspace/api-client-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { AccessSection } from "@/components/plan-editor/access-section";
import { AmenitiesSection } from "@/components/plan-editor/amenities-section";
import { BasicsSection } from "@/components/plan-editor/basics-section";
import { CreditsSection } from "@/components/plan-editor/credits-section";
import { PricingSection } from "@/components/plan-editor/pricing-section";
import { ChangePriceDialog } from "@/components/plan-editor/change-price-dialog";
import {
  canUnlockAccess,
  canUnlockAmenities,
  canUnlockCredits,
  canUnlockPricing,
  computeDirtySections,
  parsePlanEditorUrl,
  type AccessState,
  type AmenitiesState,
  type BasicsState,
  type Cadence,
  type CreditsState,
  type PeriodOverride,
  type PlanEditorState,
  type PricingState,
  type ResourceType,
} from "@/lib/plan-editor";
import {
  formatPeriodCycle,
  parsePeriodCycle,
} from "@/lib/plan-editor/period-cycle";

/**
 * Phase 25 — Plan Editor page shell + 5 section components.
 *
 * Covers: PLAN25-01..03 (route + ?spaceId pre-scope + header), ACCORD25-01
 * (5 sections render in CONTEXT-locked order with real fields),
 * ACCORD25-02 (controlled Accordion + stripDisabled guard),
 * ACCORD25-03 (auto-open next section when prereq flips true),
 * ACCORD25-04 (locked trigger visuals), GATE25-01/02 (progressive lock
 * predicates), SAVE25-01..05 (per-section Save fan-out),
 * DIRTY25-01/02 (dirty header indicator).
 *
 * Wave 3 — Plan 25-04 adds per-section Save buttons + progressive lock + dirty.
 * Plan 25-06 retired the legacy popup editor and added the unsaved-changes
 * navigation guard.
 */

// 5 accordion sections — final labels per CONTEXT.
const SECTIONS = [
  { id: "basics", label: "Basics" },
  { id: "pricing", label: "Pricing" },
  { id: "amenities", label: "Amenities" },
  { id: "credits", label: "Credits" },
  { id: "access", label: "Access" },
] as const;

function emptyPlanEditorState(spaceId: number | null): PlanEditorState {
  return {
    basics: {
      planName: "",
      cadence: "",
      resourceType: "",
      monthlyPrice: "",
      validityDays: "",
      status: "Active",
      showOnWebsite: false,
      minimumCommitmentMonths: "",
      setupFees: "",
      hsnCode: "",
      requiresApproval: false,
      spaceId,
      basicsSaved: false,
    },
    pricing: {
      securityDepositRequired: false,
      securityDepositAmount: "",
      taxRatePct: "",
      // Phase 26 (UI26-03): periodOverrides now starts empty. Operator adds
      // rows via the Pricing section UI; baseline row reflects basics.monthlyPrice.
      periodOverrides: [],
      showArchived: false,
      pricingSaved: false,
    },
    amenities: {
      dedicatedAmenitiesText: "",
      commonAmenitiesText: "",
      amenitiesSaved: false,
    },
    credits: {
      confRoomHours: "",
      meetRoomHours: "",
      creditsSaved: false,
    },
    access: {
      secondaryAccess: [],
      requiresBusinessRegistration: false,
      requiresGstRegistration: false,
      businessRegistrationIncluded: false,
      gstRegistrationIncluded: false,
      businessRegistrationCost: "",
      businessRegistrationCostPeriod: "Annual",
      gstRegistrationCost: "",
      gstRegistrationCostPeriod: "Annual",
      mailingServiceAllowed: false,
      conferenceRoomAccessAllowed: false,
      complimentaryPrintAccess: false,
      complimentaryStationary: false,
      meetingRoomAccessAllowed: false,
      conferenceRoomHoursPerMonth: "",
      meetingRoomHoursPerMonth: "",
      printPagesPerMonth: "",
      stationaryItemsPerMonth: "",
      conferenceRoomQuotaValue: "",
      conferenceRoomQuotaUnit: "Hours",
      conferenceRoomQuotaPeriod: "Month",
      meetingRoomQuotaValue: "",
      meetingRoomQuotaUnit: "Hours",
      meetingRoomQuotaPeriod: "Month",
      blackPrintPages: "",
      blackPrintOverageRate: "",
      colorPrintPages: "",
      colorPrintOverageRate: "",
      blackPrintEnabled: false,
      colorPrintEnabled: false,
      accessSaved: false,
    },
  };
}

export default function PlanEditorPage() {
  // wouter v3 has no useSearch — parse window.location.search directly.
  // parsePlanEditorUrl is the source of truth for mode + planId + spaceId.
  const [pathname, navigate] = useLocation();
  const parsed = useMemo(
    () =>
      parsePlanEditorUrl(
        pathname,
        typeof window !== "undefined" ? window.location.search : "",
      ),
    [pathname],
  );
  const { mode, planId, spaceId } = parsed;
  const { toast } = useToast();

  // Single source-of-truth aggregator. Section components are dumb;
  // we thread per-section value+onChange below.
  const [state, setState] = useState<PlanEditorState>(() =>
    emptyPlanEditorState(spaceId),
  );

  // Initial snapshot for dirty tracking (Pitfall 4 — set after hydration commits).
  const initialSnapshot = useRef<PlanEditorState>(state);

  // Page-level saving boolean — single in-flight gate disables every Save.
  const [saving, setSaving] = useState(false);
  // 2026-06-09 — track which section is currently in "edit after save" mode.
  // null = no override; sections fall back to their saved-flag for the disabled gate.
  const [editingSection, setEditingSection] = useState<
    "basics" | "pricing" | "amenities" | "credits" | "access" | null
  >(null);

  const updateBasics = useCallback(
    (next: BasicsState) => setState((prev) => ({ ...prev, basics: next })),
    [],
  );
  const updatePricing = useCallback(
    (next: PricingState) => setState((prev) => ({ ...prev, pricing: next })),
    [],
  );
  const updateAmenities = useCallback(
    (next: AmenitiesState) =>
      setState((prev) => ({ ...prev, amenities: next })),
    [],
  );
  const updateCredits = useCallback(
    (next: CreditsState) => setState((prev) => ({ ...prev, credits: next })),
    [],
  );
  const updateAccess = useCallback(
    (next: AccessState) => setState((prev) => ({ ...prev, access: next })),
    [],
  );

  // ── Edit-mode hydration queries ─────────────────────────────────────────
  const isEdit = mode === "edit" && planId !== null;
  const [showChangePrice, setShowChangePrice] = useState(false);

  const planQuery = useGetInventoryPlan(planId ?? 0, {
    query: {
      enabled: isEdit,
      queryKey: getGetInventoryPlanQueryKey(planId ?? 0),
    },
  });
  // Phase 26: useListInventoryPlanPeriods signature is now
  // (id, params?, options?). includeArchived is part of params; we omit it
  // (server defaults to Active rows only).
  const periodsQuery = useListInventoryPlanPeriods(planId ?? 0, undefined, {
    query: {
      enabled: isEdit,
      queryKey: getListInventoryPlanPeriodsQueryKey(planId ?? 0),
    },
  });
  const accessQuery = useListPlanSecondaryAccess(planId ?? 0, {
    query: {
      enabled: isEdit,
      queryKey: getListPlanSecondaryAccessQueryKey(planId ?? 0),
    },
  });
  const entitlementsQuery = useListPlanEntitlements(
    isEdit && planId !== null ? { planId } : undefined,
    {
      query: {
        enabled: isEdit,
        queryKey: getListPlanEntitlementsQueryKey(
          isEdit && planId !== null ? { planId } : undefined,
        ),
      },
    },
  );
  // Items query — enabled in both create + edit so the Credits missing-items
  // banner can fire as soon as Pricing is unlocked (operator may need to add
  // synthetic items in another tab).
  const itemsQuery = useListInventoryItems(
    {},
    {
      query: {
        queryKey: getListInventoryItemsQueryKey({}),
      },
    },
  );

  // Effect 1: plan → basics + pricing scalars + amenities + access scalars
  const planData = planQuery.data;
  useEffect(() => {
    if (!planData) return;
    const p = planData;
    setState((prev) => {
      const next: PlanEditorState = {
        ...prev,
        basics: {
          ...prev.basics,
          planName: p.planName ?? "",
          cadence: (p.cadence ?? "") as Cadence,
          resourceType: (p.resourceType ?? "") as ResourceType,
          monthlyPrice: p.monthlyPrice != null ? String(p.monthlyPrice) : "",
          validityDays: p.validityDays != null ? String(p.validityDays) : "",
          status: (p.status ?? "Active") as "Active" | "Archived",
          showOnWebsite: p.showOnWebsite ?? false,
          minimumCommitmentMonths:
            p.minimumCommitmentMonths != null
              ? String(p.minimumCommitmentMonths)
              : "",
          setupFees: p.setupFees != null ? String(p.setupFees) : "",
          hsnCode: p.hsnCode ?? "",
          requiresApproval: p.requiresApproval ?? false,
          spaceId: p.spaceId ?? prev.basics.spaceId,
          basicsSaved: true,
        },
        pricing: {
          ...prev.pricing,
          taxRatePct: p.taxRatePct != null ? String(p.taxRatePct) : "",
          securityDepositRequired: p.securityDepositRequired ?? false,
          securityDepositAmount:
            p.securityDepositAmount != null
              ? String(p.securityDepositAmount)
              : "",
          pricingSaved: true,
        },
        amenities: {
          ...prev.amenities,
          dedicatedAmenitiesText: (p.dedicatedAmenities ?? []).join(", "),
          commonAmenitiesText: (p.commonAmenities ?? []).join(", "),
          amenitiesSaved: true,
        },
        access: {
          ...prev.access,
          requiresBusinessRegistration: p.requiresBusinessRegistration ?? false,
          requiresGstRegistration: p.requiresGstRegistration ?? false,
          businessRegistrationIncluded: (p as { businessRegistrationIncluded?: boolean }).businessRegistrationIncluded ?? false,
          gstRegistrationIncluded: (p as { gstRegistrationIncluded?: boolean }).gstRegistrationIncluded ?? false,
          businessRegistrationCost: (p as { businessRegistrationCost?: number | string | null }).businessRegistrationCost != null ? String((p as { businessRegistrationCost?: number | string | null }).businessRegistrationCost) : "",
          businessRegistrationCostPeriod: (p as { businessRegistrationCostPeriod?: string | null }).businessRegistrationCostPeriod ?? "Annual",
          gstRegistrationCost: (p as { gstRegistrationCost?: number | string | null }).gstRegistrationCost != null ? String((p as { gstRegistrationCost?: number | string | null }).gstRegistrationCost) : "",
          gstRegistrationCostPeriod: (p as { gstRegistrationCostPeriod?: string | null }).gstRegistrationCostPeriod ?? "Annual",
          mailingServiceAllowed: (p as { mailingServiceAllowed?: boolean }).mailingServiceAllowed ?? false,
          conferenceRoomAccessAllowed: (p as { conferenceRoomAccessAllowed?: boolean }).conferenceRoomAccessAllowed ?? false,
          complimentaryPrintAccess: (p as { complimentaryPrintAccess?: boolean }).complimentaryPrintAccess ?? false,
          complimentaryStationary: (p as { complimentaryStationary?: boolean }).complimentaryStationary ?? false,
          meetingRoomAccessAllowed: (p as { meetingRoomAccessAllowed?: boolean }).meetingRoomAccessAllowed ?? false,
          conferenceRoomHoursPerMonth: (p as { conferenceRoomHoursPerMonth?: number | null }).conferenceRoomHoursPerMonth != null ? String((p as { conferenceRoomHoursPerMonth?: number | null }).conferenceRoomHoursPerMonth) : "",
          meetingRoomHoursPerMonth: (p as { meetingRoomHoursPerMonth?: number | null }).meetingRoomHoursPerMonth != null ? String((p as { meetingRoomHoursPerMonth?: number | null }).meetingRoomHoursPerMonth) : "",
          printPagesPerMonth: (p as { printPagesPerMonth?: number | null }).printPagesPerMonth != null ? String((p as { printPagesPerMonth?: number | null }).printPagesPerMonth) : "",
          stationaryItemsPerMonth: (p as { stationaryItemsPerMonth?: number | null }).stationaryItemsPerMonth != null ? String((p as { stationaryItemsPerMonth?: number | null }).stationaryItemsPerMonth) : "",
          conferenceRoomQuotaValue: (p as { conferenceRoomQuotaValue?: number | null }).conferenceRoomQuotaValue != null ? String((p as { conferenceRoomQuotaValue?: number | null }).conferenceRoomQuotaValue) : "",
          conferenceRoomQuotaUnit: (p as { conferenceRoomQuotaUnit?: string | null }).conferenceRoomQuotaUnit ?? "Hours",
          conferenceRoomQuotaPeriod: (p as { conferenceRoomQuotaPeriod?: string | null }).conferenceRoomQuotaPeriod ?? "Month",
          meetingRoomQuotaValue: (p as { meetingRoomQuotaValue?: number | null }).meetingRoomQuotaValue != null ? String((p as { meetingRoomQuotaValue?: number | null }).meetingRoomQuotaValue) : "",
          meetingRoomQuotaUnit: (p as { meetingRoomQuotaUnit?: string | null }).meetingRoomQuotaUnit ?? "Hours",
          meetingRoomQuotaPeriod: (p as { meetingRoomQuotaPeriod?: string | null }).meetingRoomQuotaPeriod ?? "Month",
          blackPrintPages: (p as { blackPrintPages?: number | null }).blackPrintPages != null ? String((p as { blackPrintPages?: number | null }).blackPrintPages) : "",
          blackPrintOverageRate: (p as { blackPrintOverageRate?: number | string | null }).blackPrintOverageRate != null ? String((p as { blackPrintOverageRate?: number | string | null }).blackPrintOverageRate) : "",
          colorPrintPages: (p as { colorPrintPages?: number | null }).colorPrintPages != null ? String((p as { colorPrintPages?: number | null }).colorPrintPages) : "",
          colorPrintOverageRate: (p as { colorPrintOverageRate?: number | string | null }).colorPrintOverageRate != null ? String((p as { colorPrintOverageRate?: number | string | null }).colorPrintOverageRate) : "",
          blackPrintEnabled: (p as { blackPrintEnabled?: boolean }).blackPrintEnabled ?? false,
          colorPrintEnabled: (p as { colorPrintEnabled?: boolean }).colorPrintEnabled ?? false,
          accessSaved: true,
        },
      };
      // DIRTY25-02: snapshot the hydrated state so the dirty set starts empty.
      initialSnapshot.current = next;
      return next;
    });
  }, [planData]);

  // Effect 2: period overrides → pricing.periodOverrides
  //
  // Phase 26 (UI26-03 / UI26-04): each fetched row's cycle string is normalized
  // through parsePeriodCycle so legacy enums (Quarterly / Six_Month / Yearly /
  // Monthly) hydrate forward into {periodValue, periodUnit}. Defensive read on
  // Phase 26 server fields (price, validityDays, status) — these become
  // first-class on the InventoryPlanPeriod schema once Plan 26-02 (server
  // contract) ships; until then they may be absent and we fall back to
  // priceMode='discount' / status='Active'.
  const periodsData = periodsQuery.data;
  useEffect(() => {
    if (!periodsData) return;
    setState((prev) => {
      const hydrated: PeriodOverride[] = [];
      for (const row of periodsData) {
        let parsed;
        try {
          parsed = parsePeriodCycle(row.cycle);
        } catch (e) {
          // Data corruption — log + skip row rather than crash the editor.
          // eslint-disable-next-line no-console
          console.warn(
            `[plan-editor] Skipping period row ${row.id} with unparseable cycle "${row.cycle}":`,
            e,
          );
          continue;
        }
        // Defensive reads — Phase 26 server contract widens these; Phase 27
        // adds `seats` (defaults to 1 when omitted, back-compat with pre-Phase-27
        // rows). isLegacyAbsolute marks Phase-26 absolute-price rows so the
        // Pricing section renders them read-only with a "Legacy absolute" badge.
        const r = row as unknown as {
          id: number;
          discountPct?: number | null;
          price?: number | null;
          securityDepositAmount?: number | null;
          validityDays?: number | null;
          status?: "Active" | "Archived" | null;
          seats?: number | null;
        };
        const priceMode: "discount" | "price" =
          r.price != null ? "price" : "discount";
        const isLegacyAbsolute = priceMode === "price" && r.price != null;
        hydrated.push({
          id: r.id,
          periodValue: parsed.value,
          periodUnit: parsed.unit,
          seats: r.seats != null && r.seats >= 1 ? r.seats : 1,
          priceMode,
          discountPct:
            priceMode === "discount" && r.discountPct != null
              ? String(r.discountPct)
              : "",
          price:
            priceMode === "price" && r.price != null ? String(r.price) : "",
          securityDepositRequired:
            r.securityDepositAmount != null && r.securityDepositAmount > 0,
          securityDepositAmount:
            r.securityDepositAmount != null
              ? String(r.securityDepositAmount)
              : "",
          validityDays: r.validityDays != null ? String(r.validityDays) : "",
          status: (r.status ?? "Active") as "Active" | "Archived",
          isLegacyAbsolute,
        });
      }
      const next: PlanEditorState = {
        ...prev,
        pricing: {
          ...prev.pricing,
          periodOverrides: hydrated,
        },
      };
      initialSnapshot.current = next;
      return next;
    });
  }, [periodsData]);

  // Effect 3: secondary access → access.secondaryAccess
  const accessData = accessQuery.data;
  useEffect(() => {
    if (!accessData) return;
    setState((prev) => {
      const next: PlanEditorState = {
        ...prev,
        access: {
          ...prev.access,
          secondaryAccess: accessData.map((r) => ({
            resourceType: r.resourceType,
            discountPct: r.discountPct != null ? String(r.discountPct) : "",
          })),
        },
      };
      initialSnapshot.current = next;
      return next;
    });
  }, [accessData]);

  // Effect 4: entitlements + items → credits (name-match on Conference + Meeting)
  const entitlementsData = entitlementsQuery.data;
  const itemsData = itemsQuery.data;
  useEffect(() => {
    if (!entitlementsData || !itemsData) return;
    const confItem = itemsData.find((i) =>
      /conference\s*room/i.test(i.name ?? ""),
    );
    const meetItem = itemsData.find((i) => /meeting\s*room/i.test(i.name ?? ""));
    const confEnt = confItem
      ? entitlementsData.find((e) => e.itemId === confItem.id)
      : undefined;
    const meetEnt = meetItem
      ? entitlementsData.find((e) => e.itemId === meetItem.id)
      : undefined;
    setState((prev) => {
      const next: PlanEditorState = {
        ...prev,
        credits: {
          confRoomHours:
            confEnt?.quotaQuantity != null
              ? String(confEnt.quotaQuantity)
              : "",
          meetRoomHours:
            meetEnt?.quotaQuantity != null
              ? String(meetEnt.quotaQuantity)
              : "",
          creditsSaved: true,
        },
      };
      initialSnapshot.current = next;
      return next;
    });
  }, [entitlementsData, itemsData]);

  // ── Progressive-lock unlock map (GATE25-01/02) ──────────────────────────
  const unlocked = useMemo(
    () => ({
      pricing: canUnlockPricing(state.basics),
      amenities: canUnlockAmenities(state.pricing, state.basics),
      credits: canUnlockCredits(state),
      access: canUnlockAccess(state),
    }),
    [state],
  );

  // Controlled openSection — single-open accordion per operator request
  // (one section at a time; opening another auto-closes the prior).
  // Empty string = all collapsed.
  const [openSection, setOpenSection] = useState<string>("basics");
  const handleAccordionChange = useCallback(
    (next: string) => {
      // Defense-in-depth: if the target is locked, ignore the change.
      if (next && next !== "basics" && !unlocked[next as keyof typeof unlocked]) {
        return;
      }
      setOpenSection(next);
    },
    [unlocked],
  );

  // ACCORD25-03 — auto-open the next section as soon as its prereq flips true.
  // (Single-open mode: setting the value auto-closes the prior section.)
  const prevUnlockedRef = useRef(unlocked);
  useEffect(() => {
    const prev = prevUnlockedRef.current;
    if (!prev.pricing && unlocked.pricing) setOpenSection("pricing");
    else if (!prev.amenities && unlocked.amenities) setOpenSection("amenities");
    else if (!prev.credits && unlocked.credits) setOpenSection("credits");
    else if (!prev.access && unlocked.access) setOpenSection("access");
    prevUnlockedRef.current = unlocked;
  }, [unlocked]);

  // ── Per-section save fan-out (SAVE25-01..05) ────────────────────────────
  const createPlan = useCreateInventoryPlan();
  const updatePlan = useUpdateInventoryPlan();
  const upsertPeriod = useUpsertInventoryPlanPeriod();
  // Phase 26 legacy DELETE-by-cycle kept only for the legacy code path; new
  // Phase-27 archive flow uses DELETE-by-id to disambiguate when multiple
  // (seats) rows share the same cycle string.
  const _deletePeriodLegacy = useDeleteInventoryPlanPeriod();
  void _deletePeriodLegacy;
  const deletePeriodById = useDeleteInventoryPlanPeriodById();
  const upsertAccess = useUpsertPlanSecondaryAccess();
  const deleteAccess = useDeletePlanSecondaryAccess();
  const createEntitlement = useCreatePlanEntitlement();
  const updateEntitlement = useUpdatePlanEntitlement();

  // Helper — coerce common API errors to a user-readable string.
  function errorMessage(err: unknown, fallback: string): string {
    if (err && typeof err === "object" && "response" in err) {
      const r = (err as { response?: { data?: { error?: string } } }).response;
      if (r?.data?.error) return r.data.error;
    }
    if (err instanceof Error) return err.message;
    return fallback;
  }

  // Section-bottom "Save & Continue" -> Basics handler.
  const saveBasics = useCallback(async () => {
    setSaving(true);
    try {
      const trimmedName = state.basics.planName.trim();
      const monthly = parseFloat(state.basics.monthlyPrice);
      if (!trimmedName || !Number.isFinite(monthly) || monthly <= 0) {
        toast({
          title: "Fix Basics before saving",
          description: "Plan Name + Monthly Price > 0 are required.",
          variant: "destructive",
        });
        return;
      }

      const basicsBody = {
        planName: trimmedName,
        cadence: state.basics.cadence as "Recurring" | "Non_Recurring",
        resourceType: state.basics.resourceType
          ? (state.basics.resourceType as
              | "Workstation"
              | "Private_Cabin"
              | "Meeting_Room"
              | "Conference_Room")
          : undefined,
        monthlyPrice: monthly,
        validityDays:
          state.basics.cadence === "Non_Recurring" && state.basics.validityDays
            ? Number(state.basics.validityDays)
            : null,
        status: state.basics.status,
        showOnWebsite: state.basics.showOnWebsite,
        minimumCommitmentMonths: state.basics.minimumCommitmentMonths
          ? Number(state.basics.minimumCommitmentMonths)
          : null,
        setupFees: state.basics.setupFees
          ? parseFloat(state.basics.setupFees)
          : 0,
        hsnCode: state.basics.hsnCode.trim() || null,
        requiresApproval: state.basics.requiresApproval,
      };

      if (mode === "create") {
        const result = await createPlan.mutateAsync({
          data: { ...basicsBody, spaceId: state.basics.spaceId ?? null },
        });
        // SAVE25-01 — flip basicsSaved synchronously BEFORE navigate so the
        // unlocked memo reads the saved state on the next render (Pitfall 3).
        const next: PlanEditorState = {
          ...state,
          basics: { ...state.basics, basicsSaved: true },
        };
        setState(next);
        initialSnapshot.current = next;
        toast({ title: "Basics saved", description: "Pricing unlocked." });
        // Promote URL: /billing/plans/new → /billing/plans/:id (wouter replace).
        navigate(`/billing/plans/${result.id}`, { replace: true });
      } else if (planId !== null) {
        await updatePlan.mutateAsync({ id: planId, data: basicsBody });
        const next: PlanEditorState = {
          ...state,
          basics: { ...state.basics, basicsSaved: true },
        };
        setState(next);
        initialSnapshot.current = next;
        // After save, collapse Basics so Pricing's auto-open takes focus.
        setEditingSection(null);
        setOpenSection("pricing");
        toast({ title: "Basics saved" });
      }
    } catch (err) {
      toast({
        title: errorMessage(err, "Save failed"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [state, mode, planId, createPlan, updatePlan, navigate, toast]);

  // SAVE25-02 / UI26-04 — Pricing fan-out (plan scalar + 4-branch period diff).
  //
  // Phase 26 period override diff branches (per row):
  //   1. id present + status flipped Active->Archived  → DELETE (server soft-archive)
  //   2. id present + status='Active' + row changed    → upsert (PUT)
  //   3. id absent  + status='Active' + has data       → upsert (PUT — server creates)
  //   4. id present + status='Archived' + unchanged    → noop
  //                                                      (Show Archived display toggle only)
  //
  // Body payload matches Plan 26-02 server contract (cycle derived via
  // formatPeriodCycle; price XOR discountPct routed by priceMode).
  const savePricing = useCallback(async () => {
    if (planId === null) return;
    setSaving(true);
    try {
      // 1. Plan-level pricing fields.
      await updatePlan.mutateAsync({
        id: planId,
        data: {
          taxRatePct: state.pricing.taxRatePct
            ? parseFloat(state.pricing.taxRatePct)
            : null,
          securityDepositRequired: state.pricing.securityDepositRequired,
          securityDepositAmount:
            state.pricing.securityDepositRequired &&
            state.pricing.securityDepositAmount
              ? parseFloat(state.pricing.securityDepositAmount)
              : null,
        },
      });

      // 2. Period overrides 4-branch diff.
      const initialOverrides = initialSnapshot.current.pricing.periodOverrides;
      const initialById = new Map<number, PeriodOverride>(
        initialOverrides
          .filter((r): r is PeriodOverride & { id: number } => r.id != null)
          .map((r) => [r.id, r]),
      );

      // Compare meaningful fields (excluding `status` — archive branch owns it).
      // Phase 27: seats is part of the diff so changing it triggers an upsert.
      function rowChanged(initial: PeriodOverride, current: PeriodOverride): boolean {
        const a = {
          periodValue: initial.periodValue,
          periodUnit: initial.periodUnit,
          seats: initial.seats,
          discountPct: initial.discountPct,
          securityDepositRequired: initial.securityDepositRequired,
          securityDepositAmount: initial.securityDepositAmount,
          validityDays: initial.validityDays,
        };
        const b = {
          periodValue: current.periodValue,
          periodUnit: current.periodUnit,
          seats: current.seats,
          discountPct: current.discountPct,
          securityDepositRequired: current.securityDepositRequired,
          securityDepositAmount: current.securityDepositAmount,
          validityDays: current.validityDays,
        };
        return JSON.stringify(a) !== JSON.stringify(b);
      }

      // Phase 27 savePricing always writes discountPct (which may be 0 for
      // baseline-like rows); price stays null. seats is always included so
      // the server's (planId, cycle, seats) match-key routes the UPDATE to
      // the right row (Plan 27-03 server-side guarantee).
      function buildBody(row: PeriodOverride) {
        return {
          discountPct: Number(row.discountPct) || 0,
          price: null,
          seats: row.seats,
          securityDepositRequired: row.securityDepositRequired,
          securityDepositAmount:
            row.securityDepositRequired && row.securityDepositAmount
              ? Number(row.securityDepositAmount)
              : null,
          validityDays: row.validityDays ? Number(row.validityDays) : null,
        };
      }

      for (const row of state.pricing.periodOverrides) {
        // Skip rows that have not yet committed a cycle value (empty input).
        if (row.periodValue === "" || !Number.isFinite(Number(row.periodValue))) {
          continue;
        }
        const cycleStr = formatPeriodCycle(
          Number(row.periodValue),
          row.periodUnit,
        );
        const initial = row.id != null ? initialById.get(row.id) : undefined;

        // Branch 1: hydrated row flipped Active -> Archived → soft-archive
        // via the Phase 27 DELETE-by-id endpoint (avoids cycle-string ambiguity
        // when multiple seats rows share the same period).
        if (
          row.id != null &&
          initial?.status === "Active" &&
          row.status === "Archived"
        ) {
          await deletePeriodById.mutateAsync({
            id: planId,
            periodRowId: row.id,
          });
          continue;
        }

        // Branch 4: hydrated row stayed Archived with no edits → noop.
        if (row.id != null && row.status === "Archived" && initial && !rowChanged(initial, row)) {
          continue;
        }

        // Branch 2 + 3: Active row, either new (no id) or hydrated + changed → upsert.
        const isActiveNew = row.id == null && row.status === "Active";
        const isActiveChanged =
          row.id != null && row.status === "Active" && (!initial || rowChanged(initial, row));
        // Edge: hydrated Archived row with edits (reactivation pending) — also upsert.
        const isReactivated =
          row.id != null && row.status === "Active" && initial?.status === "Archived";

        // Legacy absolute rows hydrated from Phase 26 are display-only — operator
        // may archive them (Branch 1 above) but never upsert their payload back.
        if (row.isLegacyAbsolute) continue;

        if (isActiveNew || isActiveChanged || isReactivated) {
          // Phase 27 — new rows are discount-mode (discount may be 0 for
          // baseline-like rows). Skip rows missing seats defensively.
          if (!row.seats || row.seats < 1) continue;
          await upsertPeriod.mutateAsync({
            id: planId,
            cycle: cycleStr as "Quarterly" | "Six_Month" | "Yearly",
            data: buildBody(row) as Parameters<
              typeof upsertPeriod.mutateAsync
            >[0]["data"],
          });
        }
      }

      const next: PlanEditorState = {
        ...state,
        pricing: { ...state.pricing, pricingSaved: true },
      };
      setState(next);
      initialSnapshot.current = next;
      setEditingSection(null);
      setOpenSection("amenities");
      toast({
        title: "Pricing saved",
        description: "Amenities, Credits, Access unlocked.",
      });
    } catch (err) {
      toast({
        title: errorMessage(err, "Save failed"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [state, planId, updatePlan, upsertPeriod, deletePeriodById, toast]);

  // SAVE25-03 — Amenities fan-out (single PATCH on string[] arrays).
  const saveAmenities = useCallback(async () => {
    if (planId === null) return;
    setSaving(true);
    try {
      const dedicated = state.amenities.dedicatedAmenitiesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const common = state.amenities.commonAmenitiesText
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await updatePlan.mutateAsync({
        id: planId,
        data: {
          dedicatedAmenities: dedicated.length > 0 ? dedicated : null,
          commonAmenities: common.length > 0 ? common : null,
        },
      });
      const next: PlanEditorState = {
        ...state,
        amenities: { ...state.amenities, amenitiesSaved: true },
      };
      setState(next);
      initialSnapshot.current = next;
      setEditingSection(null);
      setOpenSection("credits");
      toast({ title: "Amenities saved" });
    } catch (err) {
      toast({
        title: errorMessage(err, "Save failed"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [state, planId, updatePlan, toast]);

  // SAVE25-04 — Credits fan-out (name-match Conference/Meeting Room items;
  // silent skip when items absent — UI Alert surfaces it).
  const saveCredits = useCallback(async () => {
    if (planId === null) return;
    setSaving(true);
    try {
      const items = itemsQuery.data ?? [];
      const ents = entitlementsQuery.data ?? [];
      const defs: Array<{
        nameMatch: (n: string) => boolean;
        hours: string;
      }> = [
        {
          nameMatch: (n) =>
            n.toLowerCase().includes("conference") &&
            n.toLowerCase().includes("room"),
          hours: state.credits.confRoomHours,
        },
        {
          nameMatch: (n) =>
            n.toLowerCase().includes("meeting") &&
            n.toLowerCase().includes("room"),
          hours: state.credits.meetRoomHours,
        },
      ];
      for (const def of defs) {
        if (!def.hours || Number(def.hours) <= 0) continue;
        const item = items.find((i) => def.nameMatch(i.name ?? ""));
        if (!item) continue; // Pitfall 6 — silent skip; Alert surfaces this.
        const existingEnt = ents.find((e) => e.itemId === item.id);
        if (existingEnt) {
          await updateEntitlement.mutateAsync({
            id: existingEnt.id,
            data: { quotaQuantity: Number(def.hours) },
          });
        } else {
          await createEntitlement.mutateAsync({
            data: {
              planId,
              itemId: item.id,
              quotaQuantity: Number(def.hours),
              quotaPeriod: "per_month",
              carryForward: false,
            },
          });
        }
      }
      const next: PlanEditorState = {
        ...state,
        credits: { ...state.credits, creditsSaved: true },
      };
      setState(next);
      initialSnapshot.current = next;
      setEditingSection(null);
      setOpenSection("access");
      toast({ title: "Credits saved" });
    } catch (err) {
      toast({
        title: errorMessage(err, "Save failed"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [
    state,
    planId,
    itemsQuery.data,
    entitlementsQuery.data,
    createEntitlement,
    updateEntitlement,
    toast,
  ]);

  // SAVE25-05 — Access fan-out (registration flags + secondary access diff).
  const saveAccess = useCallback(async () => {
    if (planId === null) return;
    setSaving(true);
    try {
      // 1. Registration switches.
      await updatePlan.mutateAsync({
        id: planId,
        data: {
          requiresBusinessRegistration:
            state.access.requiresBusinessRegistration,
          requiresGstRegistration: state.access.requiresGstRegistration,
          businessRegistrationIncluded: state.access.businessRegistrationIncluded,
          gstRegistrationIncluded: state.access.gstRegistrationIncluded,
          businessRegistrationCost: state.access.businessRegistrationCost ? Number(state.access.businessRegistrationCost) : null,
          businessRegistrationCostPeriod: state.access.businessRegistrationIncluded ? (state.access.businessRegistrationCostPeriod || null) : null,
          gstRegistrationCost: state.access.gstRegistrationCost ? Number(state.access.gstRegistrationCost) : null,
          gstRegistrationCostPeriod: state.access.gstRegistrationIncluded ? (state.access.gstRegistrationCostPeriod || null) : null,
          mailingServiceAllowed: state.access.mailingServiceAllowed,
          conferenceRoomAccessAllowed: state.access.conferenceRoomAccessAllowed,
          complimentaryPrintAccess: state.access.complimentaryPrintAccess,
          complimentaryStationary: state.access.complimentaryStationary,
          meetingRoomAccessAllowed: state.access.meetingRoomAccessAllowed,
          conferenceRoomHoursPerMonth: state.access.conferenceRoomHoursPerMonth ? Number(state.access.conferenceRoomHoursPerMonth) : null,
          meetingRoomHoursPerMonth: state.access.meetingRoomHoursPerMonth ? Number(state.access.meetingRoomHoursPerMonth) : null,
          printPagesPerMonth: state.access.printPagesPerMonth ? Number(state.access.printPagesPerMonth) : null,
          stationaryItemsPerMonth: state.access.stationaryItemsPerMonth ? Number(state.access.stationaryItemsPerMonth) : null,
          conferenceRoomQuotaValue: state.access.conferenceRoomQuotaValue ? Number(state.access.conferenceRoomQuotaValue) : null,
          conferenceRoomQuotaUnit: state.access.conferenceRoomQuotaUnit || null,
          conferenceRoomQuotaPeriod: state.access.conferenceRoomQuotaPeriod || null,
          meetingRoomQuotaValue: state.access.meetingRoomQuotaValue ? Number(state.access.meetingRoomQuotaValue) : null,
          meetingRoomQuotaUnit: state.access.meetingRoomQuotaUnit || null,
          meetingRoomQuotaPeriod: state.access.meetingRoomQuotaPeriod || null,
          blackPrintPages: state.access.blackPrintPages ? Number(state.access.blackPrintPages) : null,
          blackPrintOverageRate: state.access.blackPrintOverageRate ? Number(state.access.blackPrintOverageRate) : null,
          colorPrintPages: state.access.colorPrintPages ? Number(state.access.colorPrintPages) : null,
          colorPrintOverageRate: state.access.colorPrintOverageRate ? Number(state.access.colorPrintOverageRate) : null,
          blackPrintEnabled: state.access.blackPrintEnabled,
          colorPrintEnabled: state.access.colorPrintEnabled,
        },
      });
      // 2. Secondary access diff — upsert kept rows, delete rows that were
      //    hydrated but removed.
      const initialAccess = initialSnapshot.current.access.secondaryAccess;
      const desiredTypes = new Set(
        state.access.secondaryAccess
          .filter((r) => r.resourceType && r.discountPct !== "")
          .map((r) => r.resourceType),
      );
      for (const row of state.access.secondaryAccess) {
        if (!row.resourceType || row.discountPct === "") continue;
        await upsertAccess.mutateAsync({
          id: planId,
          resourceType: row.resourceType as
            | "Workstation"
            | "Private_Cabin"
            | "Meeting_Room"
            | "Conference_Room",
          data: { discountPct: parseFloat(row.discountPct) },
        });
      }
      for (const oldRow of initialAccess) {
        if (oldRow.resourceType && !desiredTypes.has(oldRow.resourceType)) {
          await deleteAccess.mutateAsync({
            id: planId,
            resourceType: oldRow.resourceType,
          });
        }
      }
      const next: PlanEditorState = {
        ...state,
        access: { ...state.access, accessSaved: true },
      };
      setState(next);
      initialSnapshot.current = next;
      setEditingSection(null);
      setOpenSection("");
      toast({ title: "Access saved" });
    } catch (err) {
      toast({
        title: errorMessage(err, "Save failed"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [state, planId, updatePlan, upsertAccess, deleteAccess, toast]);

  // ── Dirty tracking (DIRTY25-01/02) ──────────────────────────────────────
  const dirtySet = useMemo(
    () => computeDirtySections(initialSnapshot.current, state),
    [state],
  );
  const anyDirty = dirtySet.size > 0;

  // ── Unsaved-changes navigation guard (GUARD25-01/02) ────────────────────
  // anyDirty is the single source of truth — when true, in-app navigate
  // calls are intercepted and the browser-level beforeunload listener
  // attaches. When the operator confirms discard, navigation completes;
  // cancel keeps them on the page.
  const { guardedNavigate, pendingNav, confirmNav, cancelNav } =
    useUnsavedChangesGuard(anyDirty);

  // ── Credits missing-items detection (Pitfall 6 — soft warning) ──────────
  const missingCreditItems = useMemo(() => {
    const items = itemsQuery.data;
    if (!items) return false;
    const hasConf = items.some((i) =>
      /conference\s*room/i.test(i.name ?? ""),
    );
    const hasMeet = items.some((i) => /meeting\s*room/i.test(i.name ?? ""));
    return !hasConf || !hasMeet;
  }, [itemsQuery.data]);

  // ── Render state machine ────────────────────────────────────────────────
  const isLoading = mode === "edit" && planQuery.isLoading;
  const isNotFound =
    mode === "edit" &&
    !planQuery.isLoading &&
    (planQuery.error != null || planQuery.data == null);

  const plan = planQuery.data;
  const headerTitle =
    mode === "create"
      ? state.basics.planName || "New Plan"
      : (plan?.planName ?? state.basics.planName ?? "Loading…");

  return (
    <div className="p-6">
      {/* Breadcrumb — routes through guardedNavigate so unsaved changes raise
          the AlertDialog instead of silently discarding the operator's work. */}
      <div className="mb-4 flex items-center gap-1 text-sm text-muted-foreground">
        <button
          type="button"
          onClick={() => guardedNavigate("/billing/plans")}
          className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
          data-testid="plan-editor-breadcrumb"
        >
          <ChevronLeft className="h-4 w-4" />
          Plans
        </button>
        {spaceId !== null && (
          <span className="ml-1 text-muted-foreground">
            (for Space {spaceId})
          </span>
        )}
      </div>

      {/* Header — title + dirty indicator + status badge */}
      <div className="mb-6 flex items-center gap-3">
        <h1
          className="text-2xl font-semibold tracking-tight"
          data-testid="plan-editor-title"
        >
          {headerTitle}
        </h1>
        {anyDirty && (
          <span
            className="inline-block h-2 w-2 rounded-full bg-orange-500"
            title={`Unsaved changes in: ${[...dirtySet].join(", ")}`}
            data-testid="plan-editor-dirty-dot"
          />
        )}
        {mode === "edit" && plan?.status && (
          <Badge
            variant={plan.status === "Active" ? "default" : "secondary"}
            data-testid="plan-editor-status-badge"
          >
            {plan.status}
          </Badge>
        )}
      </div>

      {/* Not-found empty state */}
      {isNotFound && (
        <div className="rounded-md border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground">
            Plan not found.{" "}
            <Link
              href="/billing/plans"
              className="text-primary underline hover:no-underline"
            >
              Return to Plans list
            </Link>
            .
          </p>
        </div>
      )}

      {/* Loading skeleton — one row per accordion section */}
      {isLoading && !isNotFound && (
        <div className="space-y-2">
          {SECTIONS.map((s) => (
            <Skeleton key={s.id} className="h-12 w-full" />
          ))}
        </div>
      )}

      {/* Accordion shell — 5 sections in CONTEXT-locked order */}
      {!isLoading && !isNotFound && (
        <Accordion
          type="single"
          collapsible
          value={openSection}
          onValueChange={handleAccordionChange}
          className="w-full space-y-2"
          data-testid="plan-editor-accordion"
        >
          <AccordionItem
            value="basics"
            className="rounded-md border px-3"
            data-testid="plan-editor-item-basics"
          >
            <AccordionTrigger className="py-3 hover:no-underline">
              <span className="text-sm font-semibold">Basics</span>
            </AccordionTrigger>
            <AccordionContent>
              <BasicsSection
                value={state.basics}
                onChange={updateBasics}
                spaceLocked={parsed.spaceId !== null}
                onSave={saveBasics}
                saving={saving}
                disabled={state.basics.basicsSaved && editingSection !== "basics"}
                onEdit={() => { setEditingSection("basics"); setOpenSection("basics"); }}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="pricing"
            className={cn(
              "rounded-md border px-3",
              !unlocked.pricing && "opacity-60",
            )}
            data-testid="plan-editor-item-pricing"
          >
            <AccordionTrigger
              disabled={!unlocked.pricing}
              className="py-3 hover:no-underline"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Pricing</span>
                {!unlocked.pricing && (
                  <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    Save Basics first
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <PricingSection
                value={state.pricing}
                onChange={updatePricing}
                basics={state.basics}
                onSave={savePricing}
                saving={saving}
                onFocusBasics={() => setOpenSection("basics")}
                onBasicsChange={(patch) =>
                  updateBasics({ ...state.basics, ...patch })
                }
                disabled={state.pricing.pricingSaved && editingSection !== "pricing"}
                onEdit={() => { setEditingSection("pricing"); setOpenSection("pricing"); }}
                priceLocked={isEdit}
                onChangePrice={() => setShowChangePrice(true)}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="amenities"
            className={cn(
              "rounded-md border px-3",
              !unlocked.amenities && "opacity-60",
            )}
            data-testid="plan-editor-item-amenities"
          >
            <AccordionTrigger
              disabled={!unlocked.amenities}
              className="py-3 hover:no-underline"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Amenities</span>
                {!unlocked.amenities && (
                  <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    Save Pricing first
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <AmenitiesSection
                value={state.amenities}
                onChange={updateAmenities}
                onSave={saveAmenities}
                saving={saving}
                disabled={state.amenities.amenitiesSaved && editingSection !== "amenities"}
                onEdit={() => { setEditingSection("amenities"); setOpenSection("amenities"); }}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="credits"
            className={cn(
              "rounded-md border px-3",
              !unlocked.credits && "opacity-60",
            )}
            data-testid="plan-editor-item-credits"
          >
            <AccordionTrigger
              disabled={!unlocked.credits}
              className="py-3 hover:no-underline"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Credits</span>
                {!unlocked.credits && (
                  <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    Save Pricing first
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <CreditsSection
                value={state.credits}
                onChange={updateCredits}
                onSave={saveCredits}
                saving={saving}
                missingItems={missingCreditItems}
                disabled={state.credits.creditsSaved && editingSection !== "credits"}
                onEdit={() => { setEditingSection("credits"); setOpenSection("credits"); }}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem
            value="access"
            className={cn(
              "rounded-md border px-3",
              !unlocked.access && "opacity-60",
            )}
            data-testid="plan-editor-item-access"
          >
            <AccordionTrigger
              disabled={!unlocked.access}
              className="py-3 hover:no-underline"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  Access &amp; Discounts
                </span>
                {!unlocked.access && (
                  <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    Save Pricing first
                  </span>
                )}
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <AccessSection
                value={state.access}
                onChange={updateAccess}
                onSave={saveAccess}
                saving={saving}
                planId={planId}
                spaceId={state.basics.spaceId}
                disabled={state.access.accessSaved && editingSection !== "access"}
                onEdit={() => { setEditingSection("access"); setOpenSection("access"); }}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* GUARD25-01 — unsaved-changes AlertDialog. Renders only when
          guardedNavigate has stashed a pendingNav (i.e., operator tried to
          navigate away while anyDirty=true). */}
      <AlertDialog
        open={pendingNav !== null}
        onOpenChange={(open) => {
          if (!open) cancelNav();
        }}
      >
        <AlertDialogContent data-testid="plan-editor-unsaved-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes on this plan. Leave and discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNav}>
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmNav}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isEdit && planId !== null && planQuery.data && (
        <ChangePriceDialog
          open={showChangePrice}
          onOpenChange={setShowChangePrice}
          plan={{
            id: planId,
            planName: planQuery.data.planName,
            monthlyPrice: planQuery.data.monthlyPrice ?? 0,
          }}
          onChanged={() => planQuery.refetch()}
        />
      )}
    </div>
  );
}
